import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Image as ImageIcon,
  FolderArchive,
  ShieldCheck,
  Cloud,
  Boxes,
  ScrollText,
  HardDrive,
  History,
  Layers3,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: History,
    title: 'IPM 10g — the origin',
    detail:
      'Oracle Imaging and Process Management (IPM) 10g was the original AP invoice repository product — image artifacts, metadata model, retention rules, and workflow integration. Many WebCenter AP installs trace their repository lineage back to an IPM 10g implementation.',
  },
  {
    icon: ImageIcon,
    title: '11g — the WebCenter rebrand',
    detail:
      'In the 11g release, IPM was rebranded as Oracle WebCenter Imaging (WCI) and joined the WebCenter product family alongside WebCenter Content (UCM), WebCenter Capture, and WebCenter Forms Recognition. The repository semantics carried forward.',
  },
  {
    icon: Layers3,
    title: '12c — convergence into WCC',
    detail:
      "Starting in 12c, the standalone Imaging server was folded into Oracle WebCenter Content. WCI became the Imaging component of WCC — running on the same WebLogic domain, sharing the same content repository, and using WCC's File Store Provider for image storage.",
  },
  {
    icon: Cloud,
    title: '14c — cloud-native repository',
    detail:
      'In WebCenter 14c, the Imaging component continues to live inside WCC and gains first-class OCI Object Storage support via the Object Storage Migration Tool. The AP repository pattern is preserved; the storage tier modernizes.',
  },
];

const STAKES = [
  {
    icon: ImageIcon,
    title: 'The image artifacts themselves',
    detail:
      'Years of invoice TIFFs, PDFs, and supporting documents — every page captured by WebCenter Forms Recognition or scanned into WebCenter Enterprise Capture and committed to the repository. Often millions of files, growing every month.',
  },
  {
    icon: FolderArchive,
    title: 'The metadata model',
    detail:
      'The Application definitions, fields, and security model that make every image findable and tie it back to the EBS Payables invoice, the Fusion FBDI load, or the SOA composite that processed it. The metadata is what turns an image into evidence.',
  },
  {
    icon: ShieldCheck,
    title: 'Audit, retention, and compliance rules',
    detail:
      'Retention categories, holds, disposition rules, and the audit trail that satisfies SOX, SOC 2, healthcare HIPAA, and industry regulators. Any forward path has to preserve the rules — not just the files — or the compliance posture resets to zero.',
  },
];

const FORWARD_PATHS = [
  {
    label: 'Path A',
    title: 'Lift-and-shift the existing 12c install to OCI Marketplace',
    body:
      'Keep WebCenter Content 12c (including the Imaging component) on the same release; move the deployment to OCI via the Oracle-published Marketplace image. WCI stays exactly as it is — same Applications, same metadata model, same File Store Provider configuration — running on OCI Compute instead of on-prem WebLogic.',
    wciChange:
      'No change to the Imaging application layer. File store can optionally move to OCI Object Storage via the Object Storage Migration Tool. The December 2026 Premier Support clock still applies — this path buys runway, not new runway.',
    link: '/solutions/webcenter-oci-marketplace-vs-modernize',
    linkLabel: 'Compare lift-and-shift vs modernize',
  },
  {
    label: 'Path A+',
    title: '12c → 14c upgrade with OCI Object Storage migration',
    body:
      "Out-of-place domain directory upgrade to WebCenter 14c. The Imaging component is upgraded inside WCC; Applications, fields, and security carry forward; the file tier moves to OCI Object Storage. Buys approximately 8 years of supported runway on Oracle's Lifetime Support Policy.",
    wciChange:
      'The Imaging component is re-deployed in the new 14c domain. WCI Applications and metadata model migrate forward via the Upgrade Assistant. File Store Provider configuration is reviewed and pointed at OCI Object Storage. Retention rules carry forward inside WCC Records.',
    link: '/solutions/webcenter-14c-upgrade',
    linkLabel: 'See the 12c → 14c upgrade detail',
  },
  {
    label: 'Path B',
    title: 'Modernize off WebCenter to AI-driven AP automation',
    body:
      'Replace the AP automation layer with a modern AI-native platform while preserving Oracle EBS or Fusion as the system of record. The historical WCI repository is migrated forward — images, metadata, audit history, retention rules — into the modern platform or into an archival OCI Object Storage tier governed by the same rules.',
    wciChange:
      'WCI is decommissioned as the live AP repository, but the historical repository is preserved with its metadata model and retention rules intact. Active AP processing moves to the modern platform; historical retrievability and audit response are unchanged.',
    link: '/solutions/modernize-oracle-webcenter',
    linkLabel: 'See the full modernization path',
  },
];

const OBJECT_STORAGE_STEPS = [
  {
    step: '1',
    title: 'File Store Provider audit',
    body:
      "WebCenter Content stores images through the File Store Provider abstraction — the same abstraction WCI inherits as the Imaging component. Step one is an audit of the current FSP configuration: storage rules, partitioning, vault paths, weblayout, and any custom storage rules added over the years.",
  },
  {
    step: '2',
    title: 'OCI Object Storage tier sizing',
    body:
      'Total object count, average object size, growth rate, retrieval frequency, and retention horizon drive bucket layout, storage tier choice (Standard vs Infrequent Access vs Archive), and cross-region replication decisions. Repositories above ~500K files are where the migration economics start to matter materially.',
  },
  {
    step: '3',
    title: 'Object Storage Migration Tool run',
    body:
      "Oracle ships an Object Storage Migration Tool built directly on top of the File Store Provider foundation. It exports source files and metadata into OCI Object Storage with verification. Runs in batches; the source FSP stays online during migration so the AP team isn't blocked.",
  },
  {
    step: '4',
    title: 'Cutover and verification',
    body:
      'WCC is reconfigured to read from the Object Storage tier; sample retrieval and audit-trail tests confirm metadata, retention categories, and security ACLs survived. Source files are retained until verification is complete, then disposed per the customer retention policy.',
  },
];

const SCENARIOS = [
  {
    icon: History,
    title: '11g IPM that never made the jump to 12c',
    body:
      "The original IPM 10g or 11g install is still live — a separate Imaging server alongside WebCenter Content, with its own database schemas and its own admin UI. The 12c convergence into WCC was deferred. Now the December 2026 cliff applies to a two-server architecture that needs both a convergence and a forward-path decision in the same window.",
  },
  {
    icon: Boxes,
    title: 'WCI customizations layered on top',
    body:
      "Custom WCI Applications, custom input agents, custom viewer components, custom security models, and custom integration scripts have accumulated over the years. The forward-path decision has to surface every customization and decide which transfer to 14c, which translate to a modern platform, and which can be retired entirely.",
  },
  {
    icon: HardDrive,
    title: 'Repository at scale — migration cost is the question',
    body:
      'Millions of invoices, terabytes of image data, retention horizons measured in decades. The forward-path question is not "can we migrate" — it is "what does the migration cost, how long does it take, and what is the cutover risk profile." The answer hinges on FSP layout, network bandwidth, and tier strategy.',
  },
  {
    icon: ShieldCheck,
    title: 'Compliance team wants retention-rule confirmation',
    body:
      'SOX, SOC 2, HIPAA, or industry-specific records retention. Compliance signs off only if retention categories, holds, disposition rules, and the audit trail provably survive migration. The forward-path plan needs a section the compliance team can read and approve before any cutover.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle killing WebCenter Imaging?',
    a: 'No — the Imaging component is included in Oracle WebCenter Content 12c and continues in 14c (release 14.1.2). What is ending is Oracle Fusion Middleware 12c Premier Support in December 2026, which affects the platform underneath WCI, not the WCI product line itself. WebCenter 14c carries approximately 8 years of supported runway (Premier through 2030, Extended through 2033) per Oracle\'s Lifetime Support Policy.',
  },
  {
    q: 'What happens to IPM 11g installations after December 2026?',
    a: 'IPM 11g customers face a compounded decision: the 12c convergence into WebCenter Content (which folded IPM in as the Imaging component) plus the 12c-to-14c forward-path question. Extended Support for 11g ended years ago, so these installs are typically on Sustaining Support or Market Driven Support today. The practical options are the same three forward paths — OCI Marketplace lift-and-shift of a 12c install, 12c-to-14c upgrade, or modernization to an AI-driven AP platform.',
  },
  {
    q: 'How does WebCenter Imaging relate to WebCenter Content?',
    a: 'Starting in 12c, WCI became the Imaging component of WebCenter Content — running on the same WebLogic domain, sharing the same content repository, and using WCC\'s File Store Provider for image storage. In 10g and 11g they were separate products with separate servers, separate schemas, and separate admin UIs — IPM/WCI handled image-centric AP repository workloads while WCC (UCM) handled general document management. In 14c the convergence is the default state.',
  },
  {
    q: 'Can we migrate to OCI Object Storage without changing the application layer?',
    a: 'Yes — the File Store Provider abstraction in WebCenter Content lets the storage tier change underneath the application without touching WCI Applications, fields, security, or workflows. Oracle ships an Object Storage Migration Tool that exports the existing file store to OCI Object Storage with metadata and verification. The application continues to read and write through FSP; only the backing tier changes. This works on 12c-on-OCI and is the recommended target tier on 14c.',
  },
  {
    q: 'How do retention rules transfer through a migration?',
    a: 'Retention categories, holds, disposition rules, and the audit trail are managed in WebCenter Content Records and transfer with the WCC database during any in-place or out-of-place upgrade. They are stored in the WCC schema, not at the file-system layer. For a Path B modernization off WebCenter, the retention rules need to be re-expressed in the target platform — every category, every hold, every disposition rule, mapped one-to-one and approved by the compliance team before cutover.',
  },
  {
    q: 'What does a typical WCI migration project cost and timeline look like?',
    a: 'Typical WCI migration projects run 3-9 months depending on repository scale, customization depth, integration count, and whether the project is bundled with a 12c-to-14c upgrade. The cost drivers are Inventory & Assessment effort, FSP-to-Object-Storage migration runtime, retention-rule mapping work, and parallel-run UAT. EZ Cloud sizes engagement pricing per situation on a Decision Call — the Inventory & Assessment phase typically establishes the right number for the specific implementation.',
  },
  {
    q: 'Is there a "do nothing" option?',
    a: 'Technically yes — 12c continues to run after Premier Support ends in December 2026 and after Extended Support ends in December 2027. Practically, the "do nothing" option becomes Sustaining Support (limited patching, no general bug fixes) or Market Driven Support (yearly renewable, more expensive than Extended). For most regulated AP repositories the compliance posture and audit-defensibility implications make a forward-path decision the right answer within the 2026-2027 window.',
  },
  {
    q: 'Can EZ Cloud help if we are going off WebCenter entirely?',
    a: 'Yes — Path B engagements are the modernization-off-WebCenter scenario specifically, where the historical WCI repository migrates forward into a modern AI-driven AP platform or an archival OCI Object Storage tier with metadata model, retention rules, and audit history preserved. Active AP processing moves to the modern platform. Andrew has 20+ years specifically inside Oracle WebCenter (Content, Imaging, Capture, Forms Recognition) and reads the existing implementation directly to scope the forward path.',
  },
];

const ModernizeWebCenterImaging = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Modernize Oracle WebCenter Imaging (WCI / IPM) — 14c, OCI, and Replacement Paths | EZ Cloud',
    description:
      'Oracle WebCenter Imaging (WCI), formerly Oracle Imaging and Process Management (IPM), is the AP invoice repository layer of every WebCenter AP install. With Fusion Middleware 12c support ending December 2026, every WCI customer faces the same decision. EZ Cloud preserves your imaging repository, audit history, and retention rules through any forward path — 14c upgrade, OCI migration, or modernization to AI-driven AP automation.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/modernize-webcenter-imaging',
    },
    about: [
      'Oracle WebCenter Imaging',
      'WebCenter Imaging',
      'IPM',
      'Oracle Imaging Process Management',
      'WebCenter Content',
      'OCI Object Storage',
      'Accounts Payable Automation',
    ].map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Modernize Oracle WebCenter Imaging (WCI / IPM) — 14c, OCI, and Replacement Paths | EZ Cloud"
        description="Oracle WebCenter Imaging (WCI), formerly Oracle Imaging and Process Management (IPM), is the AP invoice repository layer of every WebCenter AP install. With Fusion Middleware 12c support ending December 2026, every WCI customer faces the same decision. EZ Cloud preserves your imaging repository, audit history, and retention rules through any forward path — 14c upgrade, OCI migration, or modernization to AI-driven AP automation."
        canonical="https://ezcloud.ai/solutions/modernize-webcenter-imaging"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Modernize WebCenter Imaging',
            url: 'https://ezcloud.ai/solutions/modernize-webcenter-imaging',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <FolderArchive className="w-4 h-4" aria-hidden />
              WebCenter Imaging · WCI · IPM · The repository decision
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Modernize Oracle WebCenter Imaging
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Preserve the repository, the metadata, and the audit history — through any forward path.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              WebCenter Imaging (WCI / IPM) is the invoice repository layer of every WebCenter AP
              install. Three forward paths — 14c upgrade, OCI Marketplace lift-and-shift, or
              modernize to AI-driven AP automation — all preserve your repository, retention rules,
              and audit history.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wci-modernize-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Imaging Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              WebCenter Imaging, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter Imaging (WCI) — formerly Oracle Imaging and Process Management
                  (IPM) — is the AP invoice repository layer of the Oracle WebCenter product family.
                </strong>{' '}
                It manages the image artifacts, the metadata model, the security model, and the
                audit and retention rules that turn captured invoice images into an
                audit-defensible record of every payable transaction.
              </p>
              <p>
                In 10g and 11g, IPM/WCI was a standalone product with its own server and schemas.{' '}
                <strong className="text-gray-900">
                  Starting in 12c, WCI was folded into Oracle WebCenter Content as the Imaging
                  component
                </strong>{' '}
                — sharing the WebLogic domain, the content repository, and the File Store Provider
                with the rest of WCC. The Imaging component continues in 14c with first-class OCI
                Object Storage support.
              </p>
              <p>
                With Oracle Fusion Middleware 12c Premier Support ending December 2026, every WCI
                customer faces the same decision: lift-and-shift to OCI Marketplace, upgrade to 14c,
                or modernize off WebCenter to an AI-driven AP platform. All three forward paths
                preserve the repository, the metadata model, and the audit and retention rules —
                the question is which path fits the stack, the team, and the compliance posture.
              </p>
            </div>
          </div>
        </section>

        {/* What WebCenter Imaging actually is */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The product lineage
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              What WebCenter Imaging actually is
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Four release generations of the same AP repository product, with two architectural
              shifts along the way. Knowing where the install sits on the lineage clarifies the
              forward path.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="mt-10 bg-white border border-gray-200 rounded-xl p-7 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                The AP-specific deployment pattern
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Across every release, the WCI deployment pattern for AP is the same shape:
                WebCenter Forms Recognition (WFR) — sometimes paired with WebCenter Enterprise
                Capture (ODC) — extracts invoice data from incoming images; the structured
                metadata and image artifacts are committed to the WCI repository; SOA Suite
                composites or BPM workflows orchestrate approval routing and exception handling;
                and the approved invoice is posted to the ERP via the EBS Payables Open Interface
                or a Fusion FBDI load.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The repository sits at the center of that pattern. Every step before it pours
                content into WCI; every step after it depends on WCI being the authoritative
                record. That is why the forward-path decision is, in practice, a WCI decision.
              </p>
            </div>
          </div>
        </section>

        {/* What's at stake */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What's at stake
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Three things to preserve through any forward path
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Every WebCenter AP install owns a WCI repository. The forward-path decision is really
              the question of how to preserve three different things at the same time.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {STAKES.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Three forward paths */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The forward paths
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Three forward paths, and what each one means for WCI specifically
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The December 2026 cliff frames the same three options for every WebCenter AP customer.
              The difference for WCI is in what changes at the application layer versus what stays
              the same.
            </p>
            <div className="space-y-6">
              {FORWARD_PATHS.map((p) => (
                <Card key={p.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex flex-col md:flex-row gap-5">
                    <div className="flex-shrink-0">
                      <div className="px-4 py-2 rounded-lg bg-accent/15 text-accent font-bold text-sm uppercase tracking-wide inline-block">
                        {p.label}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{p.title}</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">{p.body}</p>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        <strong className="text-gray-900">What changes for WCI:</strong>{' '}
                        {p.wciChange}
                      </p>
                      <Link
                        to={p.link}
                        className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors text-sm"
                      >
                        {p.linkLabel}
                        <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the December 2026 decision framework
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* OCI Object Storage migration */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The storage tier shift
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              OCI Object Storage migration for the WCI repository
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Oracle ships a WebCenter Content Object Storage Migration Tool built on top of the
              File Store Provider foundation. For repositories above ~500K files the pattern is
              well-established and the economics on OCI become the deciding factor.
            </p>
            <div className="space-y-5">
              {OBJECT_STORAGE_STEPS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="mt-10 bg-accent/5 border border-accent/30 rounded-xl p-7">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Cost economics on OCI
              </h3>
              <p className="text-gray-700 leading-relaxed">
                OCI Object Storage tiering — Standard, Infrequent Access, and Archive — lets
                long-tail AP records live on the tier that matches their retrieval frequency. For a
                seven-year retention horizon, the bulk of the repository typically lives in
                Infrequent Access or Archive while the most-recent rolling window sits on Standard.
                Modeling the tier mix against retrieval patterns is part of the Inventory &
                Assessment phase.
              </p>
              <div className="mt-4">
                <Link
                  to="/resources/webcenter-tco-calculator"
                  className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors text-sm"
                >
                  Model the TCO with the WebCenter TCO Calculator
                  <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Common scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four common WCI scenarios
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The conversations on Decision Calls cluster around these four scenarios. Each one
              shapes the forward-path recommendation differently.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 pt-2">{title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-sm">{body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on WCI modernization
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement options for the WCI decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Standard WCI Consulting and Forward Deployed Engineering are both available
              depending on whether the work is a fixed-fee deliverable or a deeper read of an
              install with WCI customizations and undocumented components.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 1 · WCI Assessment
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Fixed-fee repository inventory and forward plan
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Andrew reads the existing WCI implementation directly and delivers a forward-path
                  plan. Surfaces every WCI Application, custom component, integration, FSP
                  configuration, and retention rule. Plan is yours to keep regardless of which
                  delivery partner you choose for the implementation work.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 2 · Migration Planning
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Fixed-fee migration plan, OCI sizing, retention mapping
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  A scoped engagement that turns the assessment into an executable migration plan —
                  OCI Object Storage tier sizing, FSP-to-Object-Storage cutover sequence, retention
                  rule mapping, parallel-run plan, and UAT scope. Designed to be the input to
                  internal procurement and Oracle conversations.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 3 · WCI Migration Delivery
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Project-based migration delivery
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Hands-on delivery of the FSP-to-OCI-Object-Storage migration using the Oracle
                  Object Storage Migration Tool. Scoped to a defined repository, a defined target
                  bucket layout, and a defined cutover window. Founder-led; outcome-priced.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 4 · Forward Deployed Engineering
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Deep read of WCI customizations and undocumented components
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  For installs where the WCI customizations have accumulated over years and the
                  original implementers have moved on. FDE work reads custom components, custom
                  Applications, input agents, viewer extensions, and integration scripts directly
                  and reconstructs how the repository actually behaves. Output feeds the migration
                  plan with the customizations correctly accounted for.
                </p>
              </Card>
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths from the December 2026 cliff —
              including the WCI repository decision — with stack-specific considerations, eight
              diagnostic questions, and a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              <ScrollText className="w-4 h-4 mr-2" aria-hidden />
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on WebCenter Imaging
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your WebCenter Imaging decision
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist with 20+ years specifically inside Oracle WebCenter — Content
              (UCM), Imaging (IPM), Capture (ODC), and Forms Recognition (WFR). Walk through your
              WCI repository, the customizations, and the forward-path options in the context of
              your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wci-modernize-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebCenter Imaging Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ModernizeWebCenterImaging;
